import { useEffect, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertTriangle, Crown, Loader2, Save } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useVoiceChoices } from '@/api/voiceChoices'
import { useCompany, type CompanyPhoneNumber, type RecordingNoticeMode } from '@/api/company'
import {
  useUpdatePhoneNumberSettings,
  type PhoneNumberSettingsInput,
} from '@/api/phoneNumbers'
import { cn } from '@/lib/utils'
import { AdresseUrgence } from './AdresseUrgence'

/**
 * Backend impose un default en dur sur recording_notice_message :
 * "Pour l'assurance qualité, cet appel peut être enregistré."
 * Si on reçoit cette valeur exacte, on l'affiche comme vide pour ne pas la
 * confondre avec une vraie personnalisation utilisateur.
 */
const BACKEND_NOTICE_DEFAULT = "Pour l'assurance qualité, cet appel peut être enregistré."

const settingsSchema = z.object({
  voice: z.string().min(1),
  record_inbound_calls: z.boolean(),
  record_outbound_calls: z.boolean(),
  recording_notice_mode: z.enum(['auto', 'manual']),
  recording_notice_message: z.string().nullable().optional(),
  recordings_retention_days: z.number().int().min(1).max(3650),
  transcription_enabled: z.boolean(),
  ai_summary_enabled: z.boolean(),
  // L'adresse 911 a son propre formulaire (AdresseUrgence).
})

type SettingsValues = z.infer<typeof settingsSchema>

interface Props {
  number: CompanyPhoneNumber
  companyId: number
}

export function PhoneNumberSettings({ number, companyId }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()

  const { data: voiceChoices, isLoading: voicesLoading } = useVoiceChoices(number.fournisseur ?? 'twilio')
  const { data: company } = useCompany(companyId)
  const update = useUpdatePhoneNumberSettings(companyId, number.id)

  const recordingAllowed = !!(company?.recording_allowed ?? company?.recording_addon_active)

  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: mapNumberToForm(number),
  })

  // Resync quand on change de numéro sans démonter.
  useEffect(() => {
    form.reset(mapNumberToForm(number))
  }, [number.id, form])

  const noticeMode = form.watch('recording_notice_mode')
  const hasRecordingOn =
    form.watch('record_inbound_calls') || form.watch('record_outbound_calls')

  const isDirty = form.formState.isDirty
  const isSaving = update.isPending

  const handleUpgrade = () => {
    window.location.href = `/${locale}/dashboard?view=profile&focus=subscription`
  }

  const onSubmit = async (values: SettingsValues) => {
    const dirty = form.formState.dirtyFields
    const keys = Object.keys(dirty) as (keyof SettingsValues)[]
    if (keys.length === 0) return

    const payload: PhoneNumberSettingsInput = {}
    for (const key of keys) {
      let v: unknown = values[key]
      if (key === 'recording_notice_message') {
        const trimmed = typeof v === 'string' ? v.trim() : ''
        v = trimmed === '' ? null : trimmed
      }
      // @ts-expect-error - types alignés par construction
      payload[key] = v
    }

    try {
      await update.mutateAsync(payload)
      toast.success(t('manager.numbers.detail.settingsSaved'))
      form.reset(values)
    } catch (err: unknown) {
      const errCode = (err as { response?: { data?: { error?: string } } })
        ?.response?.data?.error
      if (errCode === 'recording_not_allowed_by_plan') {
        toast.error(t('manager.numbers.detail.recordingNotAllowed'))
      } else if (errCode === 'e911_address_invalid') {
        toast.error(t('manager.numbers.detail.e911AddressInvalid'))
      } else if (errCode === 'e911_sync_failed') {
        toast.error(t('manager.numbers.detail.e911SyncFailed'))
      } else {
        toast.error(t('manager.numbers.detail.settingsError'))
      }
    }
  }


  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
      {/* Une carte par option : titre, une phrase, la commande à droite (modèle Aircall / Quo). */}
      <Option
        titre={t('manager.numbers.detail.voiceLabel')}
        desc={t('manager.numbers.detail.voiceDesc', "La voix qui lit les messages et menus de ce numéro.")}
        commande={
          <Select
            value={form.watch('voice')}
            onValueChange={(v) => form.setValue('voice', v, { shouldDirty: true })}
            disabled={voicesLoading}
          >
            <SelectTrigger id="pn-voice" className="w-72 h-9">
              <SelectValue placeholder={t('manager.numbers.detail.voicePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {(voiceChoices ?? []).map((vc) => (
                <SelectItem key={vc.value} value={vc.value}>{vc.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <Option
        titre={t('manager.numbers.detail.recordingTitle')}
        desc={t('manager.numbers.detail.recordingDesc', "Les appels de ce numéro sont enregistrés et gardés dans l'historique.")}
        badge={!recordingAllowed ? (
          <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300 border-amber-200 dark:border-amber-500/30">
            <Crown className="h-3 w-3 mr-1" />
            {t('manager.numbers.detail.recordingPlanRequired')}
          </Badge>
        ) : undefined}
        commande={recordingAllowed ? (
          <Switch
            id="pn-record"
            checked={hasRecordingOn}
            onCheckedChange={(v) => {
              form.setValue('record_inbound_calls', v, { shouldDirty: true })
              form.setValue('record_outbound_calls', v, { shouldDirty: true })
            }}
          />
        ) : (
          <Button type="button" size="sm" onClick={handleUpgrade} className="bg-amber-600 hover:bg-amber-700 text-white">
            {t('manager.numbers.detail.recordingUpgradeCta')}
          </Button>
        )}
      >
        {recordingAllowed && hasRecordingOn && (
          <>
            <div className="flex flex-wrap gap-6 text-sm">
              <label className="flex items-center gap-2">
                <Switch
                  checked={form.watch('record_inbound_calls')}
                  onCheckedChange={(v) => form.setValue('record_inbound_calls', v, { shouldDirty: true })}
                />
                {t('manager.numbers.detail.recordInbound')}
              </label>
              <label className="flex items-center gap-2">
                <Switch
                  checked={form.watch('record_outbound_calls')}
                  onCheckedChange={(v) => form.setValue('record_outbound_calls', v, { shouldDirty: true })}
                />
                {t('manager.numbers.detail.recordOutbound')}
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="pn-notice-mode">{t('manager.numbers.detail.noticeMode')}</label>
                <Select
                  value={noticeMode}
                  onValueChange={(v) => form.setValue('recording_notice_mode', v as RecordingNoticeMode, { shouldDirty: true })}
                >
                  <SelectTrigger id="pn-notice-mode" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">{t('manager.numbers.detail.noticeAuto')}</SelectItem>
                    <SelectItem value="manual">{t('manager.numbers.detail.noticeManual')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="pn-retention">{t('manager.numbers.detail.retentionDays')}</label>
                <Input id="pn-retention" type="number" min={1} max={3650} className="h-9" {...form.register('recordings_retention_days', { valueAsNumber: true })} />
              </div>
            </div>
            {noticeMode === 'auto' ? (
              <div className="space-y-1">
                <label className="text-xs text-slate-500" htmlFor="pn-notice-message">{t('manager.numbers.detail.noticeMessage')}</label>
                <Textarea id="pn-notice-message" rows={2} {...form.register('recording_notice_message')} placeholder={t('manager.numbers.detail.noticeMessagePlaceholder')} />
              </div>
            ) : (
              <div className="rounded-md border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-700/50 p-2.5 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-700 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800 dark:text-yellow-200">{t('manager.numbers.detail.pipedaWarning')}</p>
              </div>
            )}
          </>
        )}
      </Option>

      <Option
        titre={t('manager.numbers.detail.transcriptionEnabled')}
        desc={t('manager.numbers.detail.transcriptionDesc', "Le texte de chaque appel, lisible dans l'historique.")}
        commande={
          <Switch
            id="pn-transcription"
            checked={form.watch('transcription_enabled')}
            onCheckedChange={(v) => {
              form.setValue('transcription_enabled', v, { shouldDirty: true })
              // The summary is made from the transcription: turning it off turns the summary off too,
              // and turning it back on leaves the summary off.
              if (!v) form.setValue('ai_summary_enabled', false, { shouldDirty: true })
            }}
          />
        }
      />

      <Option
        titre={t('manager.numbers.detail.aiSummary')}
        desc={t('manager.numbers.detail.aiSummaryDesc', "Un résumé en quelques lignes après chaque appel, à partir de la transcription.")}
        commande={
          <Switch
            id="pn-ai-summary"
            checked={form.watch('ai_summary_enabled')}
            onCheckedChange={(v) => form.setValue('ai_summary_enabled', v, { shouldDirty: true })}
            disabled={!form.watch('transcription_enabled')}
          />
        }
      />

      <AdresseUrgence number={number} companyId={companyId} />

      {/* === Save bar === */}
      {isDirty && (
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" size="sm" onClick={() => form.reset()} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" size="sm" disabled={isSaving} className="bg-[#0A192F] text-white hover:bg-[#0A192F]/90 dark:bg-white dark:text-[#0A192F] dark:hover:bg-white/90">
            {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {t('manager.numbers.detail.saveSettings')}
          </Button>
        </div>
      )}
    </form>
  )
}

/** Une option : titre, une phrase, la commande à droite ; le détail s'ouvre dessous quand il y en a. */
function Option({ titre, desc, descTone, badge, commande, children }: {
  titre: string
  desc: string
  descTone?: 'warning'
  badge?: ReactNode
  commande: ReactNode
  children?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-white flex items-center gap-2">{titre}{badge}</p>
          <p className={cn('text-xs mt-0.5', descTone === 'warning' ? 'text-amber-700 dark:text-amber-400' : 'text-slate-500 dark:text-slate-400')}>{desc}</p>
        </div>
        <div className="flex-shrink-0">{commande}</div>
      </div>
      {children && <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 space-y-3">{children}</div>}
    </div>
  )
}

function mapNumberToForm(number: CompanyPhoneNumber): SettingsValues {
  return {
    voice: number.voice || 'alice',
    record_inbound_calls: number.record_inbound_calls,
    record_outbound_calls: number.record_outbound_calls,
    recording_notice_mode: number.recording_notice_mode,
    recording_notice_message:
      number.recording_notice_message === BACKEND_NOTICE_DEFAULT
        ? ''
        : (number.recording_notice_message ?? ''),
    recordings_retention_days: number.recordings_retention_days,
    transcription_enabled: number.transcription_enabled,
    // Older numbers may still store the summary on with the transcription off: the server ignores it, show it off.
    ai_summary_enabled: number.transcription_enabled && number.ai_summary_enabled,
  }
}
